import Categorical, { ActiveCategory } from "@/models/Categorical.model";
import cloneDeep from "lodash/cloneDeep";
import { types } from "./mutations";

export default {
  // return all filters for a selected entity_id
  loadInitialData: async ({ commit }, payload) => {
    commit(types.SET_FILTER_REGISTRY, payload.selectedFilters);
    commit(types.SET_APPLIED_FILTERS, payload.appliedFilters);
    commit(types.SET_LOCKED_FILTERS, payload.lockedFilters);
  },

  /**
   * Returns the list of ids of all categorical filters applied over an entity id
   * @param {*} param0
   * @param {*} entity_model_id
   */
  getSelectedEntityFilters: async ({ state }, entity_model_id) => {
    let columns = [];
    columns = state.filterRegistry
      .filter(item => item.entity_model_id === entity_model_id)
      .map(item => item.id);
    // create the custom filter columns
    let customColumns = state.customFilterRegistry
      .filter(item => item.entity_model_id === entity_model_id)
      .map(item => item.id);
    // if present, then add to columns
    if (customColumns && customColumns.length > 0)
      columns.push(...customColumns);
    if (columns && columns.length > 0) {
      return columns;
    } else {
      return null;
    }
  },

  /**
   * Returns a list of all the entity filters applied and are locked
   * @param {Object} param0
   * @param {String} entity_model_id
   */
  getLockedEntityFilters: async ({ state }, entity_model_id) => {
    if (!(state.lockedFilters && state.lockedFilters.length > 0)) return null;
    if (!(state.appliedFilters && state.appliedFilters.length > 0)) return null;
    const entityFilters = [];
    // filter the locked categories based on entity ids
    state.lockedFilters
      .filter(item => item.entity_model_id === entity_model_id)
      .forEach(item => {
        // for each locked category, find all the applied filters of them in the applied filters
        let appliedFilters = state.appliedFilters.filter(filter => {
          return (
            filter.category_id === item.id &&
            filter.entity_model_id === item.entity_model_id
          );
        });
        // if there are any, then push it to the  entity filters list
        if (appliedFilters && appliedFilters.length > 0) {
          entityFilters.push(
            ...appliedFilters.map(ftr => ({
              colname: ftr.category_id,
              comparison: ftr.comparison,
              value: ftr.item_id,
            }))
          );
        }
      });
    return entityFilters;
  },

  /**
   * Gives the list of all filters that's being applied to save as locked filters
   */
  getAllAppliedFilters: async ({ state }, entity_model_id) => {
    if (!(state.lockedFilters && state.lockedFilters.length > 0)) return null;
    if (!(state.appliedFilters && state.appliedFilters.length > 0)) return null;
    const entityFilters = [];
    // filter the locked categories based on entity ids
    state.lockedFilters
      .filter(item => item.entity_model_id === entity_model_id)
      .forEach(item => {
        // for each locked category, find all the applied filters of them in the applied filters
        let appliedFilters = state.appliedFilters.filter(filter => {
          return (
            filter.category_id === item.id &&
            filter.entity_model_id === item.entity_model_id
          );
        });
        // if there are any, then push it to the  entity filters list
        if (appliedFilters && appliedFilters.length > 0) {
          entityFilters.push(
            ...appliedFilters.map(ftr => ({
              colname: ftr.category_id,
              comparison: ftr.comparison,
              value: ftr.item_id,
            }))
          );
        }
      });

    return entityFilters;
  },

  /**
   * Returns a payload of list of all applied entity filters for an entityModel
   * @param {Object} param0
   * @param {String} entity_model_id
   */
  getAppliedEntityFilterPayload: async ({ state }, entity_model_id) => {
    if (!state.appliedFilters) return null;

    const apppliedEntityFilters = state.appliedFilters
      .filter(item => item.entity_model_id === entity_model_id)
      .map(item => ({
        colname: item.category_id,
        comparison: item.comparison,
        value: item.item_id,
      }));

    return apppliedEntityFilters;
  },

  /**
   * Generates payload for categorical widgets associated with an entity
   * Each categorical widget will have a columns array, which will contain all the columns for the entity
   * The response will be an exploded version of the categorical widget
   * @param {} param0
   */
  createEntityFilterPayload: async ({ dispatch }) => {
    try {
      // requests the layer manager to provide the current selected metric
      const widget = await dispatch(
        "LayerManager/createPayloadForCategorical",
        null,
        { root: true }
      );
      if (!widget) return null;

      const entity_model_id = widget.meta.id;
      const columns = await dispatch(
        "FilterManager/getSelectedEntityFilters",
        entity_model_id,
        { root: true }
      );

      if (!(columns && columns.length > 0)) return [];
      if (widget.setCols) {
        widget.col = columns;
        return widget;
      } else {
        widget.setColumns(columns);
        return widget.toJSON();
      }
    } catch (error) {
      // no active property layer available
      console.error(error);
      return null;
    }
  },

  /**
   * Loads in the filter data into the store
   * Updates, existing filters
   * @param {*} param0
   * @param {Array[Object]} data
   */
  loadFilterData: async ({ state, commit }, data) => {
    if (!(data && data.length > 0)) return;
    let filters = cloneDeep(state.filterData);

    const newCategoricalFilters = data.map(item => new Categorical(item));

    newCategoricalFilters.forEach(item => {
      // check if there exists filter within the store
      // if yes, update the data
      const existingFilter = filters.find(
        filter => filter.id === item.id && filter.meta.id === item.meta.id
      );
      if (existingFilter) {
        existingFilter.data = item.data;
      } else {
        //if it's not present, add it to the filter's list
        filters.push(item);
      }
    });

    commit(types.SET_FILTER_DATA, filters);
  },

  /**
   * Toggles a filter item
   * @param {Object} param0
   * @param {ActiveCategory} filter
   */
  toggleFilter: async ({ commit, state }, filter) => {
    if (!filter) return null;

    let appliedFilters = cloneDeep(state.appliedFilters);
    if (appliedFilters && appliedFilters.length > 0) {
      // check if the filter is present in the applied list
      const isApplied = appliedFilters.find(item => filter.compare(item));
      if (isApplied) {
        // remove the filter from the filters list
        commit(
          types.SET_APPLIED_FILTERS,
          appliedFilters.filter(item => !filter.compare(item))
        );
      } else {
        appliedFilters.push(filter);
        commit(types.SET_APPLIED_FILTERS, appliedFilters);
      }
    } else {
      // if not present or is not removed add it to the list
      commit(types.SET_APPLIED_FILTERS, [filter]);
    }
    return true;
  },

  /**
   * Adds or Removes an entity filter from the filters registry
   * @param {*} param0
   * @param {*} categorical
   */
  toggleEntityFilter: async ({ commit, state }, categorical) => {
    if (!(state.filterRegistry && state.filterRegistry.length > 0)) {
      commit(types.SET_FILTER_REGISTRY, [categorical]);
    } else {
      // check if the item is already present
      const isPresent = state.filterRegistry.find(
        ({ id, entity_model_id }) =>
          id == categorical.id &&
          entity_model_id === categorical.entity_model_id
      );
      // if the item is present then remove it
      if (isPresent) {
        commit(
          types.SET_APPLIED_FILTERS,
          state.appliedFilters.filter(
            item =>
              item.category_id !== categorical.id &&
              item.entity_model_id !== categorical.entity_model_id
          )
        );
        commit(types.SET_FILTER_REGISTRY, [
          ...state.filterRegistry.filter(
            item =>
              !(
                item.id === categorical.id &&
                item.entity_model_id === categorical.entity_model_id
              )
          ),
        ]);
        // remove the filters from the store
        commit(
          types.SET_FILTER_DATA,
          state.filterData.filter(
            item =>
              !(
                item.meta.id === categorical.entity_model_id &&
                item.id === categorical.id
              )
          )
        );
      } else {
        // else append to the selected filters
        commit(types.SET_FILTER_REGISTRY, [
          ...state.filterRegistry,
          categorical,
        ]);
      }
    }
  },

  /**
   * Creates a custom filter and adds it to the registry
   * @param {*} param0
   * @param {*} categorical
   */
  createCustomFilter: async ({ commit, state }, categorical) => {
    if (
      !(state.customFilterRegistry && state.customFilterRegistry.length > 0)
    ) {
      commit(types.SET_CUSTOM_FILTER_REGISTRY, [categorical]);
    } else {
      const isPresent = state.customFilterRegistry.find(
        ({ id, entity_model_id }) =>
          id == categorical.id &&
          entity_model_id === categorical.entity_model_id
      );

      // update the existing one if it's present
      if (isPresent) {
        commit(types.SET_CUSTOM_FILTER_REGISTRY, [
          ...state.customFilterRegistry.filter(
            item =>
              !(
                item.id === categorical.id &&
                item.entity_model_id === categorical.entity_model_id
              )
          ),
          categorical,
        ]);
      }
    }
  },

  /**
   * Removes a custom filter
   */
  removeCustomFilter: async ({ commit, state }, categorical) => {
    const isPresent = state.customFilterRegistry.find(
      ({ id, entity_model_id }) =>
        id == categorical.id && entity_model_id === categorical.meta.id
    );

    // update the existing one if it's present
    if (isPresent) {
      commit(
        types.SET_APPLIED_FILTERS,
        state.appliedFilters.filter(
          item =>
            !(
              item.category_id === categorical.id &&
              item.entity_model_id === categorical.meta.id
            )
        )
      );
      commit(types.SET_CUSTOM_FILTER_REGISTRY, [
        ...state.customFilterRegistry.filter(
          item =>
            !(
              item.id === categorical.id &&
              item.entity_model_id === categorical.meta.id
            )
        ),
      ]);
      commit(
        types.SET_FILTER_DATA,
        state.filterData.filter(
          item =>
            !(
              item.meta.id === categorical.meta.id && item.id === categorical.id
            )
        )
      );
    }
  },

  /**
   * Removes all the filters for the entity model
   */
  removeFiltersForEntityModel: async ({ commit, state }, entity_model_id) => {
    // remove the filters from the registry
    commit(
      types.SET_FILTER_REGISTRY,
      state.filterRegistry.filter(item => item.id !== entity_model_id)
    );
    // remoce the filters from the custom filter registry
    commit(
      types.SET_CUSTOM_FILTER_REGISTRY,
      state.customFilterRegistry.filter(item => item.id !== entity_model_id)
    );
    // remove the filters from the store
    commit(
      types.SET_FILTER_DATA,
      state.filterData.filter(item => item.meta.id !== entity_model_id)
    );
  },

  /**
   * Locak a filter from using it
   * @param {*} param0
   */
  toggleFilterLock: async ({ commit, state }, filter) => {
    if (!(state.lockedFilters && state.lockedFilters.length > 0)) {
      commit(types.SET_LOCKED_FILTERS, [filter]);
    } else {
      // check if the filter is locked
      const isLocked = state.lockedFilters.find(
        item =>
          item.id === filter.id &&
          item.entity_model_id === filter.entity_model_id
      );
      // if locked, remove it from the filters
      if (isLocked) {
        commit(
          types.SET_LOCKED_FILTERS,
          state.lockedFilters.filter(
            item =>
              !(
                item.id === filter.id &&
                item.entity_model_id === filter.entity_model_id
              )
          )
        );
      } else {
        commit(types.SET_LOCKED_FILTERS, [...state.lockedFilters, filter]);
      }
    }
  },

  /**
   * resets the module state
   */
  reset: async ({ commit }) => {
    commit(types.reset);
  },
};
